import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';


const OPS_ROLES = [
  'admin', 'super_admin', 'delivery', 'support', 'warehouse', 'moderator', 'finance',
  'returns_manager', 'marketing', 'catalog_manager', 'vendor_onboarding', 'ad_manager',
  'api_partner', 'community_manager', 'dispute_arbitrator', 'qa_tester', 'regional_manager',
];

// Maps URL slug → role key + display info
const ROLE_MAP = {
  admin_login: { role: 'admin', label: 'Admin', color: '#7C3AED', icon: '⚙️' },
  superadmin_login: { role: 'super_admin', label: 'Super Admin', color: '#1E293B', icon: '🛡️' },
  super_admin_login: { role: 'super_admin', label: 'Super Admin', color: '#1E293B', icon: '🛡️' },
  support_login: { role: 'support', label: 'Support Agent', color: '#0891B2', icon: '🎧' },
  delivery_login: { role: 'delivery', label: 'Delivery Partner', color: '#EA580C', icon: '🚚' },
  warehouse_login: { role: 'warehouse', label: 'Warehouse Manager', color: '#65A30D', icon: '📦' },
  finance_login: { role: 'finance', label: 'Finance / Accountant', color: '#16A34A', icon: '💰' },
  moderator_login: { role: 'moderator', label: 'Content Moderator', color: '#9333EA', icon: '🔍' },
  returns_login: { role: 'returns_manager', label: 'Returns Manager', color: '#DC2626', icon: '🔄' },
  marketing_login: { role: 'marketing', label: 'Marketing Manager', color: '#E11D48', icon: '📣' },
  catalog_login: { role: 'catalog_manager', label: 'Catalog Manager', color: '#0D9488', icon: '📋' },
  onboarding_login: { role: 'vendor_onboarding', label: 'Vendor Onboarding', color: '#7C3AED', icon: '🤝' },
  ads_login: { role: 'ad_manager', label: 'Ad Manager', color: '#BE185D', icon: '🎯' },
  api_login: { role: 'api_partner', label: 'API Partner', color: '#1E293B', icon: '🔑' },
  community_login: { role: 'community_manager', label: 'Community Manager', color: '#2563EB', icon: '🌐' },
  disputes_login: { role: 'dispute_arbitrator', label: 'Dispute Arbitrator', color: '#B45309', icon: '⚖️' },
  qa_login: { role: 'qa_tester', label: 'QA Tester', color: '#15803D', icon: '✅' },
  regional_login: { role: 'regional_manager', label: 'Regional Manager', color: '#4338CA', icon: '🗺️' },
};

const DASHBOARD_MAP = {
  super_admin: '/super-admin/dashboard', admin: '/admin/dashboard',
  delivery: '/delivery/dashboard', support: '/support/dashboard',
  warehouse: '/warehouse/dashboard', moderator: '/moderator/dashboard',
  finance: '/finance/dashboard', returns_manager: '/returns-manager/dashboard',
  marketing: '/marketing/dashboard', catalog_manager: '/catalog/dashboard',
  vendor_onboarding: '/vendor-onboarding/dashboard', ad_manager: '/ad-manager/dashboard',
  api_partner: '/api-partner/dashboard', community_manager: '/community/dashboard',
  dispute_arbitrator: '/disputes/dashboard', qa_tester: '/qa-tester/dashboard',
  regional_manager: '/regional/dashboard',
};

const AdminLoginPage = () => {
  const { roleSlug } = useParams();
  const { t } = useLanguage();
  const config = ROLE_MAP[roleSlug] || { role: '', label: 'Operations Team', color: '#1E293B', icon: '🛡️' };
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(form.email, form.password);
      if (result?.user && OPS_ROLES.includes(result.user.role)) {
        navigate(DASHBOARD_MAP[result.user.role] || '/admin/dashboard');
      } else {
        setError('This account does not have operations portal access.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid credentials');
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: 'linear-gradient(135deg, #EDE9FE 0%, #F5F3FF 50%, #EFF6FF 100%)' }}>
      {/* Left Branding Panel */}
      <div style={{
        flex: '0 0 380px', background: 'linear-gradient(180deg, #1E293B 0%, #0F172A 100%)',
        display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
        padding: '60px 36px', color: '#FFF', position: 'relative', overflow: 'hidden',
      }} className="d-none d-lg-flex">
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: `radial-gradient(circle at 80% 20%, ${config.color}25 0%, transparent 50%)`, pointerEvents: 'none' }} />

        <div style={{ fontSize: '72px', marginBottom: '20px' }}>{config.icon}</div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '28px', fontWeight: 700, textAlign: 'center', marginBottom: '8px' }}>
          {t('operationsHub')}
        </h2>
        <p style={{ textAlign: 'center', opacity: 0.7, fontSize: '14px', lineHeight: 1.7, maxWidth: '280px', marginBottom: '40px' }}>
          {t('operationsHubDesc')}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', opacity: 0.6 }}>
          {['⚙️ Admin', '🎧 Support', '📦 Warehouse', '💰 Finance', '🚚 Delivery'].map(tag => (
            <span key={tag} style={{ fontSize: '11px', fontWeight: 600, background: 'rgba(255,255,255,0.1)', padding: '5px 10px', borderRadius: '16px' }}>{tag}</span>
          ))}
        </div>
      </div>

      {/* Right Form */}
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
        <div style={{ width: '100%', maxWidth: '440px' }}>
          <Link to="/" style={{ textDecoration: 'none', display: 'block', marginBottom: '8px' }}>
            <span style={{ fontSize: '28px', fontWeight: 800, color: '#B91C1C', fontFamily: "'Poppins', sans-serif" }}>
              eBuy<span style={{ color: '#EAB308' }}>Wiz</span>
            </span>
          </Link>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: '28px', color: '#1F2937', marginBottom: '4px' }}>
            {t('operationsSignIn')}
          </h1>
          <p style={{ color: config.color, fontWeight: 600, fontSize: '15px', marginBottom: '28px' }}>{config.label}</p>

          {error && <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '12px 16px', borderRadius: '10px', marginBottom: '16px', fontSize: '14px' }}>{error}</div>}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '14px', color: '#374151', marginBottom: '6px' }}>{t('emailAddress')}*</label>
              <input type="email" required style={inputStyle} placeholder="admin@ebuywiz.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '14px', color: '#374151', marginBottom: '6px' }}>{t('password')}*</label>
              <div style={{ position: 'relative' }}>
                <input type={showPassword ? 'text' : 'password'} required style={{ ...inputStyle, paddingRight: '44px' }} placeholder="Password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#9CA3AF', fontSize: '18px' }} title={showPassword ? 'Hide password' : 'Show password'}>
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>
            <div style={{ textAlign: 'right', marginBottom: '20px' }}>
              <Link to="/forgot-password" style={{ color: config.color, fontSize: '14px', fontWeight: 600, textDecoration: 'none' }}>{t('forgotPassword')}</Link>
            </div>
            <button type="submit" disabled={loading} style={{ ...btnStyle, background: '#1E293B' }}>
              {loading ? t('signingIn') : t('signIn')}
            </button>
          </form>

          <p style={{ textAlign: 'center', marginTop: '24px', fontSize: '13px', color: '#9CA3AF' }}>
            <Link to="/choose-role" style={{ color: '#9CA3AF', textDecoration: 'none' }}>{t('backToAllRoles')}</Link>
          </p>
          <p style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px', color: '#D1D5DB' }}>
            {t('opsAccountsCreated')}
          </p>
        </div>
      </div>

      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700;800&family=Playfair+Display:ital,wght@0,700;1,700&display=swap" rel="stylesheet" />
    </div>
  );
};

const inputStyle = {
  width: '100%', padding: '14px 16px', border: '2px solid #E5E7EB', borderRadius: '10px',
  fontSize: '15px', outline: 'none', boxSizing: 'border-box', fontFamily: "'Poppins', sans-serif",
};
const btnStyle = {
  width: '100%', padding: '16px', border: 'none', borderRadius: '10px',
  color: '#FFF', fontSize: '15px', fontWeight: 700, cursor: 'pointer',
  fontFamily: "'Poppins', sans-serif", letterSpacing: '0.5px',
};

export default AdminLoginPage;
